<template class="page-content">
  <ModalLayout ref="matchAppliedTagModal" modal-id="match-applied-tag-modal"
    :loading="isSavableStateLoading"
    @close="modalClosed">
    <template #modal-header>
      <h5 v-if="selectedMatch && selectedMatch.transplant_center" class="modal-title">
        {{ `Donor: ${selectedMatch.donor.code} | Match: ${selectedMatch.code}-${selectedMatch.organ.name} | TC: ${selectedMatch.transplant_center.name}
                     | Offer Date: ${selectedMatch.offer_date}` }}
      </h5>
    </template>
    <template #modal-body>
      <form @submit.prevent="addTag">
        <fieldset>
          <div class="row" v-if="selectedMatch">
            <div class="col-sm-12">
              <div class="row">
                <MultiSelectInput v-model="selectedTags"
                  name="tags"
                  label="Available Tags"
                  :options="availableTagOptions"
                  label-key="label"
                  value-key="id"
                  :multiple="true"
                  append-to="body">
                <template #option="{ option }">
                  <span :title="option.label" class="option-label-truncated">{{ option.label }}</span>
                </template>
              </MultiSelectInput>
              </div>

              <ActionToolbar :component-state="currentState"
                :primary-disabled="isSavableStateLoading"
                success-message="Successfully updated!"
                primary-button-text="Add Tags"
                :secondary-enabled="false"
                :error-message="formError" />
            </div>
          </div>
        </fieldset>
      </form>
      <hr>
      <div class="row">
        <div class="col-sm-12">
            <h2 class="font-weight-bold">Applied Tags</h2>
            <div v-if="appliedTags.length > 0" class="mt-2">
              <span v-for="tag in appliedTags" :key="tag.id" class="badge badge-info mr-2 mb-2 px-3 py-2 d-inline-flex align-items-center" :title="tag.label">
                <span class="tag-badge">{{ tag.label }}</span>
                <a href="#"
                  class="text-white ml-2"
                  @click.prevent="removeTag(tag)"
                  aria-label="Remove tag">
                  <font-awesome-icon :icon="['fas', 'times']" />
                </a>
              </span>
              <br>
            </div>
            <p v-else class="text-muted small mt-1 mb-0">No tags applied.</p>
        </div>
      </div>
    </template>
  </ModalLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
// @ts-ignore
import ModalLayout from "@/src/components/shared/ModalLayout.vue";
// @ts-ignore
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
// @ts-ignore
import { useTagStore } from '@/src/stores/tag';
// @ts-ignore
import { useMatchStore } from '@/src/stores/match';
import { Match } from "@/src/models/match";
// @ts-ignore
import MultiSelectInput from "@/src/components/shared/FormComponents/MultiSelectInput.vue";
import { useSavableState } from '@/src/composables/useSavableState';
import { useModalActions } from '@/src/composables/useModalActions';

const props = defineProps({
  matchId: { required: true, type: String }
});

const emit = defineEmits(['close', 'tags-updated']);

const tagStore = useTagStore();
const matchStore = useMatchStore();

const {
  formError,
  currentState,
  isSavableStateLoading,
  setSavableStateLoading,
  setSavableStateProcessing,
  setSavableStateIdle,
  setSavableStateBlank,
  setSavableStateSuccess,
  handleFormError,
} = useSavableState();

const matchAppliedTagModal = ref<InstanceType<typeof ModalLayout> | null>(null);

// Component state
const selectedMatch = ref<Match | null>(null);
let selectedTags = ref<any[]>([]);
const appliedTags = ref<any[]>([]);

const selectedTagObjects = computed(() => {
  if (!selectedTags.value || !Array.isArray(selectedTags.value)) return [];
  return tagStore.tagCategoryTagsList.filter((tag: any) => selectedTags.value.includes(tag.id));
});

const availableTagOptions = computed(() => {
  const appliedTagIds = new Set(appliedTags.value.map((t: any) => t.tag_id));
  return tagStore.tagCategoryTagsList.filter((tag: any) => !appliedTagIds.has(tag.tag_id));
});

async function getModalData() {
  if (!props.matchId) return;
  try {
    setSavableStateLoading();
    await tagStore.loadTagCategories();
    const response = await matchStore.loadMatch(props.matchId, false, { params: { lite: true } });
    const match = Match.buildFromAPI(response);
    selectedMatch.value = match;
    appliedTags.value = match.tags;
    setSavableStateIdle();
  } catch (error) {
    handleFormError(error);
  }
}

async function removeTag(tag: any) {
  setSavableStateProcessing();
  try {
    await tagStore.removeTagFromMatch(tag.id, props.matchId);
    await getModalData();
    emit('tags-updated');
    setSavableStateSuccess();
  } catch (error) {
    handleFormError(error);
  }
}

async function addTag() {
  setSavableStateProcessing();
  const payload = {
    match_id: props.matchId,
    tag_ids: selectedTagObjects.value.map((t: any) => t.tag_id)
  };
  try {
    await tagStore.applyTagToMatch(payload);
    await getModalData();
    emit('tags-updated');
    selectedTags.value = [];
    setSavableStateSuccess();
  } catch (error) {
    handleFormError(error);
  }
}

watch(() => props.matchId, async () => {
  await getModalData();
});

onMounted(async () => {
  selectedMatch.value = null;
  await getModalData();
});

const { showModal, closeModal, closeAllModals, modalClosed } = useModalActions(matchAppliedTagModal, emit, {
  // Bootstrap's enforceFocus blocks typing in the PrimeVue filter input, which is appended to body outside the modal DOM
  onShown: () => {
    ($ as any)(document).off('focusin.modal');
  },
  onClose: () => {
    setSavableStateBlank();
    selectedMatch.value = null;
    selectedTags.value = [];
  },
});

defineExpose({ showModal, closeModal, closeAllModals });
</script>

<style scoped>
.option-label-truncated {
  display: inline-block;
  max-width: 40ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
